import {
    type Column as TanStackColumn,
    type ColumnDef as TanStackColumnDef,
    columnFacetingFeature,
    columnFilteringFeature,
    columnOrderingFeature,
    columnSizingFeature,
    columnVisibilityFeature,
    createFacetedMinMaxValues,
    createFacetedRowModel,
    createFacetedUniqueValues,
    createFilteredRowModel,
    createPaginatedRowModel,
    createSortedRowModel,
    type FilterFn as TanStackFilterFn,
    type FilterFnOption as TanStackFilterFnOption,
    filterFns,
    globalFilteringFeature,
    type ReactTable as TanStackReactTable,
    type RowData,
    rowPaginationFeature,
    rowSelectionFeature,
    rowSortingFeature,
    sortFns,
    tableFeatures,
} from '@tanstack/react-table';

/**
 * The TanStack Table v9 feature set shared by every table in the app, plus the
 * type aliases that bind it.
 *
 * v9 no longer bundles features: each table declares the features, row models,
 * and filter/sort function registries it uses, and every public type carries
 * that feature set as its first generic argument. Components import the
 * aliases below (`ColumnDef<TData>`, `Table<TData>`, …) so they read like the
 * v8 types they replace and stay bound to one app-wide feature set.
 */

/**
 * The row surface the app's filter functions read. Structural rather than
 * `Row<AppTableFeatures, TData>` so the functions can be registered in the
 * feature set that defines `AppTableFeatures` without the type referencing
 * itself. Every app table registers `rowSelectionFeature`, so `getIsSelected`
 * is always present — the selection-preserving variants below need it, and a
 * single row type keeps both variants interchangeable in the registry.
 */
type FilterableRow = {
    readonly getValue: (columnId: string) => unknown;
    readonly getIsSelected: () => boolean;
};

// Case-insensitive "contains" for strings (works for global+column filters)
export function containsText(
    row: FilterableRow,
    columnId: string,
    filterValue: unknown,
): boolean {
    const raw = row.getValue(columnId);
    if (
        filterValue === null ||
        filterValue === undefined ||
        filterValue === ''
    ) {
        return true;
    }
    const v = String(raw ?? '').toLowerCase();
    const q = String(filterValue ?? '').toLowerCase();
    return v.includes(q);
}

// Multi-select faceted filter: handles both single values and arrays
export function inMultiSelect(
    row: FilterableRow,
    columnId: string,
    filterValue: unknown,
): boolean {
    const {
        include,
        exclude,
        includeMode = 'any',
        excludeMode = 'all',
    } = normalizeMultiSelectValue(filterValue);
    if (include.length === 0 && exclude.length === 0) {
        return true;
    }

    const cell = row.getValue(columnId);

    // Handle array cells (e.g., tags, roles)
    if (Array.isArray(cell)) {
        if (!arrIncludesFilter(includeMode, cell, include)) {
            return false;
        }
        return arrExcludesFilter(excludeMode, cell, exclude);
    }

    // Handle single values
    const isIncluded = include.length === 0 || includesValue(include, cell);
    const isExcluded = includesValue(exclude, cell);
    return isIncluded && !isExcluded;
}

function containsTextPreservingSelection(
    row: FilterableRow,
    columnId: string,
    filterValue: unknown,
): boolean {
    return row.getIsSelected() || containsText(row, columnId, filterValue);
}

function inMultiSelectPreservingSelection(
    row: FilterableRow,
    columnId: string,
    filterValue: unknown,
): boolean {
    return row.getIsSelected() || inMultiSelect(row, columnId, filterValue);
}

type MultiSelectFilterValue = {
    readonly include: Array<unknown>;
    readonly exclude: Array<unknown>;
    readonly includeMode?: 'any' | 'all';
    readonly excludeMode?: 'any' | 'all';
};

const normalizeMultiSelectValue = (
    filterValue: unknown,
): MultiSelectFilterValue => {
    if (Array.isArray(filterValue)) {
        return { include: filterValue, exclude: [] };
    }
    if (filterValue && typeof filterValue === 'object') {
        const value = filterValue as {
            include?: Array<unknown>;
            exclude?: Array<unknown>;
            includeMode?: 'any' | 'all';
            excludeMode?: 'any' | 'all';
        };
        return {
            include: Array.isArray(value.include) ? value.include : [],
            exclude: Array.isArray(value.exclude) ? value.exclude : [],
            includeMode:
                value.includeMode === 'all'
                    ? 'all'
                    : value.includeMode === 'any'
                      ? 'any'
                      : undefined,
            excludeMode:
                value.excludeMode === 'all'
                    ? 'all'
                    : value.excludeMode === 'any'
                      ? 'any'
                      : undefined,
        };
    }
    if (filterValue === null || filterValue === undefined) {
        return { include: [], exclude: [] };
    }
    return { include: [filterValue], exclude: [] };
};

const areValuesEqual = (a: unknown, b: unknown): boolean => {
    if (Object.is(a, b)) {
        return true;
    }
    if (
        (typeof a === 'object' && a !== null) ||
        (typeof b === 'object' && b !== null)
    ) {
        try {
            return JSON.stringify(a) === JSON.stringify(b);
        } catch {
            return false;
        }
    }
    return false;
};

const includesValue = (values: Array<unknown>, target: unknown): boolean =>
    values.some((item) => areValuesEqual(item, target));

// Shared helper for array includes filtering
const arrIncludesFilter = (
    mode: 'all' | 'any',
    cell: unknown,
    include: Array<unknown>,
): boolean => {
    if (include.length === 0) {
        return true;
    }
    if (Array.isArray(cell)) {
        if (cell.length === 0) {
            return false;
        }
        if (mode === 'any') {
            return cell.some((item) => includesValue(include, item));
        }
        // mode === 'all'
        return include.every((tag) => includesValue(cell, tag));
    }
    return includesValue(include, cell);
};

// Shared helper for exclude filtering
const arrExcludesFilter = (
    mode: 'all' | 'any',
    cell: unknown,
    exclude: Array<unknown>,
): boolean => {
    if (exclude.length === 0) {
        return true;
    }
    if (Array.isArray(cell)) {
        if (cell.length === 0) {
            return true;
        }
        if (mode === 'all') {
            const hasAllExcluded = exclude.every((tag) =>
                includesValue(cell, tag),
            );
            return !hasAllExcluded;
        }
        // Default: exclude if ANY excluded value is present
        return !cell.some((item) => includesValue(exclude, item));
    }
    return !includesValue(exclude, cell);
};

// The built-in registries are registered wholesale: columns that omit
// `filterFn`/`sortFn` resolve `'auto'` against them by name (`includesString`,
// `alphanumeric`, …), and the app's column defs span every value type.
const stockTableFeatures = {
    columnFacetingFeature,
    columnFilteringFeature,
    columnOrderingFeature,
    columnSizingFeature,
    columnVisibilityFeature,
    globalFilteringFeature,
    rowPaginationFeature,
    rowSelectionFeature,
    rowSortingFeature,
    facetedMinMaxValues: createFacetedMinMaxValues(),
    facetedRowModel: createFacetedRowModel(),
    facetedUniqueValues: createFacetedUniqueValues(),
    filteredRowModel: createFilteredRowModel(),
    paginatedRowModel: createPaginatedRowModel(),
    sortedRowModel: createSortedRowModel(),
    sortFns,
};

export const appTableFeatures = tableFeatures({
    ...stockTableFeatures,
    filterFns: { ...filterFns, containsText, inMultiSelect },
});

/**
 * Feature set for tables that keep selected rows visible while filters are
 * applied. v9 resolves filter functions from the static feature registry, so
 * the selection bypass is a second registry rather than a per-table override.
 */
export const appTableFeaturesPreservingSelection: AppTableFeatures = {
    ...stockTableFeatures,
    filterFns: {
        ...filterFns,
        containsText: containsTextPreservingSelection,
        inMultiSelect: inMultiSelectPreservingSelection,
    },
};

export type AppTableFeatures = typeof appTableFeatures;

export type Column<TData extends RowData, TValue = unknown> = TanStackColumn<
    AppTableFeatures,
    TData,
    TValue
>;

export type ColumnDef<
    TData extends RowData,
    TValue = unknown,
> = TanStackColumnDef<AppTableFeatures, TData, TValue>;

export type FilterFn<TData extends RowData> = TanStackFilterFn<
    AppTableFeatures,
    TData
>;

export type FilterFnOption<TData extends RowData> = TanStackFilterFnOption<
    AppTableFeatures,
    TData
>;

export type Table<TData extends RowData> = TanStackReactTable<
    AppTableFeatures,
    TData
>;

export {
    type ColumnDefTemplate,
    type ColumnFiltersState,
    type ColumnOrderState,
    type ColumnVisibilityState,
    createColumnHelper,
    flexRender,
    type OnChangeFn,
    type PaginationState,
    type RowData,
    type RowSelectionState,
    type SortingState,
    useTable,
} from '@tanstack/react-table';
